import React, { useState, useEffect, useCallback } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FormHelperText from '@mui/material/FormHelperText';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import firebaseApp from '../../../firebase-app';
import { useStringsContext } from '../../../contexts/strings';
import { useSnackbar } from 'notistack';
import { getMetadata, ref, uploadBytes } from 'firebase/storage';
import Stack from '@mui/material/Stack';
import { InputWithValidateProps } from '../../common/input/text-input-with-validate';
import { AnySchema } from 'yup';
import { SxStyle } from '../../../types';
import ImageDropzoneDialog, { ImageFile } from '../../common/image-dropzone-dialog';
import { useFieldValidation } from '../../../hooks/formValidate';

const styles: SxStyle = {
  image: {
    width: '100px',
    height: '100px',
    boxShadow: '0 2px 6px 0 rgba(0,0,0,0.24)',
    borderRadius: '1rem',
  },
};

async function getImageSize(url: string): Promise<{ height: number; width: number }> {
  const img = new Image();
  img.src = url;
  return new Promise((resolve) => {
    img.onload = () => {
      resolve({
        height: img.height,
        width: img.width,
      });
    };
  });
}

export type ImageWithValidateProps<T = { [key: string]: unknown }> = InputWithValidateProps<T> & {
  storagePath: string;
  value: string;
  name: keyof T & string;
  validation?: AnySchema; // TODO Move to InputWithValidateProps?
  isReadOnly?: boolean;
  recommendedHeight?: number;
  recommendedWidth?: number;
};

function ImageWithValidate<T>({
  storagePath,
  name,
  value,
  onValue,
  validation,
  onValidation,
  isReadOnly,
  recommendedHeight,
  recommendedWidth,
}: ImageWithValidateProps<T>) {
  const { strings } = useStringsContext();
  const { doValidate, errorMessage: errorMessage } = useFieldValidation();
  const { enqueueSnackbar } = useSnackbar();

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageAlt, setImageAlt] = useState<string>('');
  const [showDropzone, setShowDropzone] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sizeMessage, setSizeMessage] = useState<string>('');
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setImageUrl(value);
  }, [value]);

  useEffect(() => {
    const validate = async () => {
      if (validation) {
        const isValid = await doValidate(imageUrl, validation);
        onValidation?.(name, isValid, validation);
      }
    };
    validate();
  }, [doValidate, imageUrl, name, onValidation, validation]);

  const onFileSelect = useCallback(
    async (file: ImageFile) => {
      const id = Date.now().toString(36);
      const fileName = file?.name.replace(/[^a-zA-Z0-9-_.]/g, '');
      const uniqueFilename = `${id}-${fileName}`;
      const storageRef = ref(firebaseApp.storage, storagePath + `/${uniqueFilename}`);

      try {
        setIsSubmitting(true);
        setUploadErrorMessage(null);
        setSizeMessage('');

        await uploadBytes(storageRef, file);
        // Use url pointing to the Google cloud storage bucket instead of Firebase storage (due to problem with url together with CDN)
        const fileMetaData = await getMetadata(storageRef);
        const url = `https://storage.googleapis.com/${
          process.env.REACT_APP_FIREBASE_STORAGEBUCKET
        }/${encodeURIComponent(fileMetaData.fullPath)}`;

        const { height, width } = await getImageSize(url);

        if (recommendedHeight) {
          if (height > recommendedHeight) {
            setSizeMessage(strings.messages.imageTooLargeError);
          } else if (height < recommendedHeight) {
            setSizeMessage(strings.messages.imageTooSmallError);
          }
        }
        if (recommendedWidth) {
          if (width > recommendedWidth) {
            setSizeMessage(strings.messages.imageTooLargeError);
          } else if (width < recommendedWidth) {
            setSizeMessage(strings.messages.imageTooSmallError);
          }
        }

        setImageUrl(url);
        setImageAlt(fileName);

        onValue && onValue(name, url);
        enqueueSnackbar(strings.messages.uploadSuccess, {
          variant: 'success',
        });
      } catch (error: unknown) {
        setUploadErrorMessage(strings.messages.uploadErrorImage);
        enqueueSnackbar(strings.messages.uploadError, {
          variant: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [storagePath, recommendedHeight, recommendedWidth, onValue, name, enqueueSnackbar, strings]
  );

  const onRemoveImage = useCallback(() => {
    setImageUrl('');
    setImageAlt('');
    onValue && onValue(name, '');
  }, [name, onValue]);

  return (
    <>
      <Stack direction="row" spacing={4} paddingTop={1}>
        <Avatar variant="square" src={imageUrl || ''} alt={imageAlt} sx={styles.image} />
        <Stack direction="column" justifyContent="center" spacing={2}>
          <Button
            disabled={!!isReadOnly}
            variant="contained"
            color="primary"
            loading={isSubmitting}
            onClick={() => setShowDropzone(true)}
            startIcon={<CloudUploadIcon />}
          >
            {strings.actions.uploadImage}
          </Button>
          {imageUrl && (
            <Button
              onClick={onRemoveImage}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={(isReadOnly ?? false) || isSubmitting}
            >
              {strings.actions.removeImage}
            </Button>
          )}
        </Stack>
      </Stack>
      <FormHelperText error>{errorMessage || uploadErrorMessage || sizeMessage}</FormHelperText>
      <ImageDropzoneDialog
        open={showDropzone}
        onClose={() => setShowDropzone(false)}
        maxSize={2 * 1024 * 1024}
        onSave={(files) => {
          onFileSelect(files[0]);
          setShowDropzone(false);
        }}
      />
    </>
  );
}

export default ImageWithValidate;
